import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Trash2, DoorClosed, Building2, CheckCircle2, AlertTriangle,
  Clock, FileText, Rocket,
} from "lucide-react";
import { externalReportsApi, buildingsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDateTime, apiError, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function BackLink() {
  return (
    <Link to="/imports" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft className="h-4 w-4" /> Report imports
    </Link>
  );
}

export default function ExternalReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: report, loading, error, reload } = useAsync(() => externalReportsApi.getOne(id), [id]);
  const buildings = useAsync(() => buildingsApi.getAll(), []);
  const [busy, setBusy] = useState("");
  const [buildingId, setBuildingId] = useState("");

  useEffect(() => { if (report?.building_id) setBuildingId(report.building_id); }, [report?.building_id]);

  const processing = report && (report.processing_status || "ready") === "processing";
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(() => reload(), 5000);
    return () => clearInterval(t);
  }, [processing, reload]);

  if (loading) return <div><BackLink /><LoadingState label="Loading report…" /></div>;
  if (error) return <div><BackLink /><ErrorState message={error} onRetry={reload} /></div>;
  if (!report) return null;

  const doors = report.extracted_data?.doors || [];
  const bldList = Array.isArray(buildings.data) ? buildings.data : [];
  const isPromoted = report.status === "promoted";
  const failed = (report.processing_status || "ready") === "failed";

  const linkBuilding = async (bid) => {
    setBuildingId(bid);
    try { await externalReportsApi.patch(id, { building_id: bid }); await reload(); }
    catch (e) { toast.error(apiError(e, "Could not link building")); }
  };

  const doPromote = async () => {
    if (!buildingId) { toast.error("Link a building first."); return; }
    setBusy("promote");
    try {
      const res = await externalReportsApi.promote(id, { building_id: buildingId, create_inspections: true });
      const n = res.data?.created_doors?.length ?? res.data?.doors_created ?? doors.length;
      toast.success(`Promoted — ${n} door${n === 1 ? "" : "s"} added to the register`);
      await reload();
    } catch (e) { toast.error(apiError(e, "Could not promote")); }
    finally { setBusy(""); }
  };

  const doGenerateQuote = async () => {
    setBusy("quote");
    try {
      const res = await externalReportsApi.createQuote(id);
      const d = res.data || {};
      const quotes = d.quotes || [];
      if (quotes.length === 0) { toast.error("This report has no doors to quote."); return; }
      if (d.building_name) {
        toast.success(d.building_created ? `New building "${d.building_name}" created from the report` : `Matched to building "${d.building_name}"`);
      }
      if (quotes.length === 1) { navigate(`/quotes/${quotes[0].id}`); return; }
      toast.success(`${quotes.length} quote${quotes.length === 1 ? "" : "s"} ${d.existing ? "already existed" : "created"}`);
      navigate("/quotes");
    } catch (e) { toast.error(apiError(e, "Could not generate quote")); }
    finally { setBusy(""); }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this imported report?")) return;
    setBusy("delete");
    try { await externalReportsApi.remove(id); toast.success("Report deleted"); navigate("/imports"); }
    catch (e) { toast.error(apiError(e, "Could not delete")); setBusy(""); }
  };

  return (
    <div>
      <BackLink />
      <PageHeader
        title={report.filename || "Imported report"}
        subtitle={fmtDateTime(report.created_at)}
        testId="import-detail-header"
        actions={
          <Button variant="outline" size="icon" disabled={!!busy} onClick={doDelete} data-testid="import-delete-btn" className="text-red-600 hover:text-red-700">
            {busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        }
      />

      {processing && (
        <Card className="mb-4 flex items-center gap-3 border-amber-200 bg-amber-50 p-4" data-testid="import-processing">
          <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
          <div><p className="font-medium text-amber-800">Extracting doors & defects…</p><p className="text-sm text-amber-700">This can take up to a minute. The page refreshes automatically.</p></div>
        </Card>
      )}
      {failed && (
        <Card className="mb-4 flex items-center gap-3 border-red-200 bg-red-50 p-4" data-testid="import-failed">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <div><p className="font-medium text-red-800">Extraction failed</p><p className="text-sm text-red-700">{report.processing_error || "The AI could not read this report. Try re-uploading a clearer copy."}</p></div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card className="p-5">
            <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><DoorClosed className="h-5 w-5 text-primary" /> Extracted doors ({doors.length})</h3>
            {doors.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">{processing ? "Waiting for extraction…" : "No doors were extracted from this report."}</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="import-doors">
                {doors.map((d, i) => {
                  const ref = d.door_number || d.door_reference || `Door ${i + 1}`;
                  const st = String(d.status || "").toLowerCase();
                  const defects = Array.isArray(d.defects) ? d.defects : [];
                  return (
                    <li key={`${ref}-${i}`} className="py-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-slate-800">{ref}{d.location ? ` · ${d.location}` : ""}</p>
                        {st && <Badge variant="outline" className={statusTone(st)}>{prettyStatus(st)}</Badge>}
                      </div>
                      <p className="text-sm text-slate-500">{[d.floor && `Floor ${d.floor}`, d.leaf_type, d.fire_rating || d.fd_rating].filter(Boolean).join(" · ")}</p>
                      {defects.length > 0 && (
                        <ul className="mt-1 list-disc pl-5 text-sm text-slate-600">
                          {defects.slice(0, 6).map((x, j) => <li key={`def-${j}`}>{typeof x === "string" ? x : x?.description || x?.defect || JSON.stringify(x)}</li>)}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="mb-3 font-semibold text-slate-800">Target building</h3>
            <div className="space-y-2">
              <Label>Link to building</Label>
              <Select value={buildingId || "none"} onValueChange={(v) => linkBuilding(v === "none" ? "" : v)} disabled={isPromoted}>
                <SelectTrigger data-testid="import-building-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not linked</SelectItem>
                  {bldList.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {report.building_match_method && report.building_match_method !== "none" && (
                <p className="text-xs text-slate-400">Matched via {report.building_match_method}</p>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="mb-3 font-semibold text-slate-800">Actions</h3>
            {isPromoted ? (
              <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-medium">Promoted to register</span></div>
            ) : (
              <div className="space-y-2">
                <Button className="w-full" disabled={!!busy || !buildingId || processing || doors.length === 0} onClick={doPromote} data-testid="import-promote-btn">
                  {busy === "promote" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />} Promote to register
                </Button>
                <p className="text-xs text-slate-400">Creates door + inspection records under the linked building.</p>
              </div>
            )}
            <Button variant="outline" className="mt-3 w-full" disabled={!!busy || processing || doors.length === 0} onClick={doGenerateQuote} data-testid="import-quote-btn">
              {busy === "quote" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Generate quote
            </Button>
          </Card>

          {report.building_id && (
            <Link to={`/buildings/${report.building_id}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              <Building2 className="h-4 w-4" /> Open building
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
